import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import Modal from '../ui/Modal'
import { Sparkles, Loader2, Download, ChevronDown } from 'lucide-react'

export default function AIReportButton({ consultancyId, entityType = 'consultancy', entityId, reportType = 'summary', label = 'Generar informe IA' }) {
  const { profile } = useAuth()
  const [loading, setLoading]   = useState(false)
  const [report, setReport]     = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [error, setError]       = useState(null)
  const [pastReports, setPastReports] = useState([])
  const [showPast, setShowPast] = useState(false)

  async function loadPastReports() {
    const query = supabase.from('ai_reports').select('*').eq('consultancy_id', consultancyId).order('created_at', { ascending: false })
    if (entityId) query.eq('entity_id', entityId)
    const { data } = await query.limit(5)
    setPastReports(data ?? [])
  }

  async function generateReport() {
    setLoading(true)
    setError(null)
    try {
      let context = ''

      if (entityType === 'section') {
        const [{ data: sec }, { data: tasks }, { data: files }] = await Promise.all([
          supabase.from('sections').select('*, phases(name, business_units(name))').eq('id', entityId).single(),
          supabase.from('tasks').select('*').eq('section_id', entityId),
          supabase.from('files').select('file_name').eq('entity_type', 'section').eq('entity_id', entityId),
        ])
        context = `Sección: ${sec?.name}\nEtapa: ${sec?.phases?.name}\nUnidad de Negocio: ${sec?.phases?.business_units?.name ?? ''}\nDescripción: ${sec?.description ?? ''}\n\nTareas:\n${tasks?.map(t => `- [${t.status}] ${t.name}${t.description ? ': ' + t.description : ''}`).join('\n') || 'Sin tareas'}\n\nArchivos subidos:\n${files?.map(f => f.file_name).join(', ') || 'Ninguno'}`

      } else if (entityType === 'phase') {
        // Contexto ENRIQUECIDO de la etapa: pulled de TODAS las pestañas
        // visibles en PhasePage (modelo de negocio, DRL, stakeholders,
        // afirmaciones, secciones con sus tab_conclusions, tasks, archivos
        // adjuntos, comentarios, perfiles de consumidor — todo lo que el
        // consultor ve).
        const sectionIds = (await supabase.from('sections').select('id').eq('phase_id', entityId)).data?.map(s => s.id) ?? []
        const [
          { data: ph },
          { data: cons },
          { data: sections },
          { data: ficha },
          { data: drl },
          { data: stakeholders },
          { data: afirmaciones },
          { data: tabConclusions },
          { data: phaseFiles },
          { data: sectionFiles },
          { data: phaseComments },
          { data: sectionComments },
          { data: consumerProfiles },
        ] = await Promise.all([
          supabase.from('phases').select('*, business_units(name)').eq('id', entityId).single(),
          supabase.from('consultancies').select('name, client_company, industry, description').eq('id', consultancyId).maybeSingle(),
          supabase.from('sections').select('*, tasks(name, status, description)').eq('phase_id', entityId).order('section_order'),
          supabase.from('project_ficha').select('*').eq('consultancy_id', consultancyId).maybeSingle(),
          supabase.from('solicitudes_informacion').select('categoria, descripcion, estado, estado_final, razon_no_cierre, notas, responsable, fecha_limite').eq('consultancy_id', consultancyId).order('categoria').order('orden'),
          supabase.from('stakeholders').select('nombre, cargo, area, tipo_rol, prioridad, estado_entrevista, notas').eq('consultancy_id', consultancyId),
          supabase.from('afirmaciones').select('texto, seccion_nombre, estado, prioridad, fuente').eq('consultancy_id', consultancyId),
          supabase.from('section_tab_conclusions').select('section_id, tab, content').in('section_id', sectionIds.length ? sectionIds : ['00000000-0000-0000-0000-000000000000']),
          supabase.from('files').select('file_name, description, mime_type, created_at').eq('entity_type', 'phase').eq('entity_id', entityId),
          sectionIds.length
            ? supabase.from('files').select('file_name, description, entity_id').eq('entity_type', 'section').in('entity_id', sectionIds)
            : Promise.resolve({ data: [] }),
          supabase.from('comments').select('content, created_at, profiles(full_name)').eq('entity_type', 'phase').eq('entity_id', entityId).order('created_at'),
          sectionIds.length
            ? supabase.from('comments').select('content, created_at, entity_id, profiles(full_name)').eq('entity_type', 'section').in('entity_id', sectionIds).order('created_at')
            : Promise.resolve({ data: [] }),
          // Perfiles de consumidor del phase. Cada profile tiene sus PROPIAS
          // sections anidadas (Demografía, Brand adoption, Needs/Tensions...)
          // — las traemos junto con sus tab_conclusions para que la IA vea
          // las conclusiones registradas por el consultor en cada subtab.
          supabase.from('consumer_profiles')
            .select('id, name, description, profile_order, sections(id, name, status, section_order, tasks(name, status))')
            .eq('phase_id', entityId)
            .order('profile_order'),
        ])

        // Fetch tab_conclusions de las sub-sections de los consumer_profiles
        // (en una query separada para no anidar joins muy complejos)
        const profileSectionIds = (consumerProfiles ?? [])
          .flatMap(p => (p.sections ?? []).map(s => s.id))
          .filter(Boolean)
        const { data: profileSectionConcl } = profileSectionIds.length
          ? await supabase.from('section_tab_conclusions').select('section_id, tab, content').in('section_id', profileSectionIds)
          : { data: [] }

        const isEtapa0 = (ph?.phase_order === 0) || /modelo de negocio|entendimiento/i.test(ph?.name ?? '')

        // ── Cabecera ──
        context = `INFORME DE ETAPA — Estado actual y diagnóstico\n`
        context += `═══════════════════════════════════════════════\n\n`
        if (cons) {
          context += `Empresa cliente: ${cons.client_company ?? ''}\n`
          context += `Proyecto: ${cons.name ?? ''}\n`
          if (cons.industry)    context += `Industria: ${cons.industry}\n`
          if (cons.description) context += `Descripción: ${cons.description}\n`
        }
        context += `\nEtapa: ${ph?.name} (orden ${ph?.phase_order})\n`
        if (ph?.business_units?.name) context += `Unidad de Negocio: ${ph.business_units.name}\n`
        if (ph?.description)          context += `Descripción etapa: ${ph.description}\n`
        context += `Estado: ${ph?.status} · Progreso: ${ph?.progress ?? 0}%\n`

        // ── Etapa 0 específico: pestañas modelo de negocio, DRL, etc. ──
        if (isEtapa0 && ficha) {
          context += `\n━━━━━━━━━ MODELO DE NEGOCIO (Mi Modelo) ━━━━━━━━━\n`
          const camposFicha = [
            ['bm_descripcion_libre', 'Modelo en palabras propias'],
            ['bm_que_vendes',        '¿Qué vende?'],
            ['bm_a_quien',           '¿A quién?'],
            ['bm_como_entregas',     '¿Cómo entrega?'],
            ['bm_como_ganas_dinero', '¿Cómo gana dinero?'],
            ['bm_que_te_hace_unico', '¿Qué lo hace único?'],
            ['bm_supuestos',         'Supuestos no validados'],
          ]
          for (const [k, label] of camposFicha) {
            if (ficha[k]) context += `\n${label}: ${ficha[k]}\n`
          }
          if (ficha.etapa0_final_report) {
            context += `\n— Informe IA previo de Etapa 0 (resumen):\n${(ficha.etapa0_final_report ?? '').slice(0, 1500)}\n`
          }
        }

        // ── DRL: Información necesaria ──
        if (drl && drl.length > 0) {
          context += `\n━━━━━━━━━ DRL · Información necesaria (${drl.length} items) ━━━━━━━━━\n`
          const groups = {}
          for (const d of drl) {
            if (!groups[d.categoria]) groups[d.categoria] = []
            groups[d.categoria].push(d)
          }
          for (const [cat, items] of Object.entries(groups)) {
            context += `\n  ${cat}:\n`
            for (const it of items) {
              const stIni  = it.estado || 'Sin estado'
              const stFin  = it.estado_final ? ` → ${it.estado_final}` : ''
              const razon  = it.razon_no_cierre ? ` (razón: ${it.razon_no_cierre})` : ''
              context += `    [${stIni}${stFin}] ${it.descripcion}${razon}\n`
            }
          }
        }

        // ── Stakeholders ──
        if (stakeholders && stakeholders.length > 0) {
          context += `\n━━━━━━━━━ STAKEHOLDERS (${stakeholders.length}) ━━━━━━━━━\n`
          for (const s of stakeholders) {
            context += `  • ${s.nombre}${s.cargo ? ' — ' + s.cargo : ''}${s.area ? ' (' + s.area + ')' : ''}`
            if (s.tipo_rol || s.prioridad) context += ` [rol: ${s.tipo_rol ?? '?'} / prioridad: ${s.prioridad ?? '?'}]`
            if (s.estado_entrevista) context += ` · entrevista: ${s.estado_entrevista}`
            context += `\n`
            if (s.notas) context += `    notas: ${s.notas}\n`
          }
        }

        // ── Afirmaciones ──
        if (afirmaciones && afirmaciones.length > 0) {
          context += `\n━━━━━━━━━ AFIRMACIONES (${afirmaciones.length}) ━━━━━━━━━\n`
          for (const a of afirmaciones) {
            context += `  • [${a.seccion_nombre ?? '?'} · ${a.estado ?? '?'} · ${a.prioridad ?? '?'}] ${a.texto}\n`
            if (a.fuente) context += `    fuente: ${a.fuente}\n`
          }
        }

        // ── Secciones + sus tareas + sus tab_conclusions ──
        const conclByID = {}
        for (const c of (tabConclusions ?? [])) {
          if (!conclByID[c.section_id]) conclByID[c.section_id] = []
          conclByID[c.section_id].push(c)
        }
        const sectFilesByID = {}
        for (const f of (sectionFiles ?? [])) {
          if (!sectFilesByID[f.entity_id]) sectFilesByID[f.entity_id] = []
          sectFilesByID[f.entity_id].push(f)
        }
        const sectCommentsByID = {}
        for (const c of (sectionComments ?? [])) {
          if (!sectCommentsByID[c.entity_id]) sectCommentsByID[c.entity_id] = []
          sectCommentsByID[c.entity_id].push(c)
        }
        if (sections && sections.length > 0) {
          context += `\n━━━━━━━━━ SECCIONES DE LA ETAPA (${sections.length}) ━━━━━━━━━\n`
          for (const s of sections) {
            context += `\n── ${s.name} (${s.status}) ──\n`
            if (s.description) context += `descripción: ${s.description}\n`
            const tasksList = s.tasks ?? []
            if (tasksList.length > 0) {
              context += `tareas:\n`
              tasksList.forEach(t => {
                context += `  [${t.status}] ${t.name}${t.description ? ' — ' + t.description : ''}\n`
              })
            }
            const conclList = conclByID[s.id] ?? []
            if (conclList.length > 0) {
              context += `conclusiones registradas por el consultor:\n`
              conclList.forEach(c => {
                context += `  · ${c.tab}: ${(c.content ?? '').slice(0, 500)}\n`
              })
            }
            const filesList = sectFilesByID[s.id] ?? []
            if (filesList.length > 0) {
              context += `archivos adjuntos en esta sección:\n`
              filesList.forEach(f => {
                context += `  📎 ${f.file_name}${f.description ? ' — ' + f.description : ''}\n`
              })
            }
            const commList = sectCommentsByID[s.id] ?? []
            if (commList.length > 0) {
              context += `comentarios del equipo en esta sección:\n`
              commList.slice(-5).forEach(c => {
                const author = c.profiles?.full_name ?? 'usuario'
                context += `  💬 ${author}: ${(c.content ?? '').slice(0, 300)}\n`
              })
            }
          }
        }

        // ── Perfiles de consumidor (de la etapa) ──
        if (consumerProfiles && consumerProfiles.length > 0) {
          context += `\n━━━━━━━━━ PERFILES DE CONSUMIDOR (${consumerProfiles.length}) ━━━━━━━━━\n`
          context += `Cada perfil tiene sus propias sub-secciones (Demografía, Brand adoption, Needs/Tensions, Solution, etc.).\n`

          // Index conclusiones por section_id de las sub-sections de profiles
          const profileConclByID = {}
          for (const c of (profileSectionConcl ?? [])) {
            if (!profileConclByID[c.section_id]) profileConclByID[c.section_id] = []
            profileConclByID[c.section_id].push(c)
          }

          for (const profile of consumerProfiles) {
            context += `\n▸ PERFIL: ${profile.name}\n`
            if (profile.description) context += `  Descripción: ${profile.description}\n`
            const subSections = (profile.sections ?? []).sort((a, b) => (a.section_order ?? 0) - (b.section_order ?? 0))
            if (subSections.length === 0) {
              context += `  (sin sub-secciones registradas todavía)\n`
            } else {
              for (const ss of subSections) {
                context += `  ─ ${ss.name} (${ss.status})\n`
                const conclList = profileConclByID[ss.id] ?? []
                if (conclList.length > 0) {
                  for (const c of conclList) {
                    context += `    · ${c.tab}: ${(c.content ?? '').slice(0, 400)}\n`
                  }
                }
                const tasksList = ss.tasks ?? []
                if (tasksList.length > 0) {
                  const doneCount = tasksList.filter(t => t.status === 'completed' || t.status === 'done').length
                  context += `    tareas: ${doneCount}/${tasksList.length} completadas\n`
                }
              }
            }
          }
        }

        // ── Archivos y comentarios a nivel de la ETAPA (no sección) ──
        if (phaseFiles && phaseFiles.length > 0) {
          context += `\n━━━━━━━━━ ARCHIVOS ADJUNTOS DE LA ETAPA (${phaseFiles.length}) ━━━━━━━━━\n`
          phaseFiles.forEach(f => {
            context += `📎 ${f.file_name}${f.description ? ' — ' + f.description : ''} (${f.mime_type ?? '?'})\n`
          })
          context += `\nNota: solo tengo los nombres y descripciones de los archivos, no su contenido binario. Si necesito información puntual, debo pedírsela al consultor.\n`
        }
        if (phaseComments && phaseComments.length > 0) {
          context += `\n━━━━━━━━━ COMENTARIOS DEL EQUIPO EN LA ETAPA (${phaseComments.length}) ━━━━━━━━━\n`
          phaseComments.slice(-10).forEach(c => {
            const author = c.profiles?.full_name ?? 'usuario'
            const when = new Date(c.created_at).toLocaleDateString('es')
            context += `💬 ${author} (${when}): ${(c.content ?? '').slice(0, 500)}\n`
          })
        }

      } else if (entityType === 'consolidation') {
        // Gather all BUs' Stage 5 (Definición de Estrategia) + consolidation phase context
        const [{ data: cons }, { data: bus }, { data: ph }] = await Promise.all([
          supabase.from('consultancies').select('*').eq('id', consultancyId).single(),
          supabase.from('business_units').select('id, name, description').eq('consultancy_id', consultancyId).order('bu_order'),
          supabase.from('phases').select('*, sections(name, status, tasks(name, status, description))').eq('id', entityId).single(),
        ])

        context = `CONSOLIDACIÓN EN ESTRATEGIA CORPORATIVA\n\nEmpresa: ${cons?.client_company}\nProyecto: ${cons?.name}\nIndustria: ${cons?.industry ?? ''}\nDescripción: ${cons?.description ?? ''}\n\n`
        context += `════════════════════════════════════════════\nESTRATEGIAS POR UNIDAD DE NEGOCIO (Etapa 5)\n════════════════════════════════════════════\n`

        for (const bu of (bus ?? [])) {
          const { data: stratPhase } = await supabase
            .from('phases')
            .select('name, status, progress, sections(name, status, description, tasks(name, status, description))')
            .eq('business_unit_id', bu.id)
            .eq('phase_order', 5)
            .maybeSingle()

          context += `\n╔══════════════════════════════\n║ UDN: ${bu.name}\n╚══════════════════════════════\n`
          if (bu.description) context += `Descripción: ${bu.description}\n`

          if (stratPhase) {
            context += `Etapa de Estrategia: ${stratPhase.name} (${stratPhase.status}, ${stratPhase.progress}%)\n`
            stratPhase.sections?.forEach(s => {
              context += `\n  ── ${s.name} (${s.status}) ──\n`
              if (s.description) context += `  ${s.description}\n`
              s.tasks?.forEach(t => { context += `    [${t.status}] ${t.name}${t.description ? ': ' + t.description : ''}\n` })
            })
          } else {
            context += `  [Etapa de estrategia aún no iniciada]\n`
          }
        }

        context += `\n\n════════════════════════════════════════════\nFASE DE CONSOLIDACIÓN CORPORATIVA\n════════════════════════════════════════════\n`
        ph?.sections?.forEach(s => {
          context += `\n── ${s.name} (${s.status}) ──\n`
          s.tasks?.forEach(t => { context += `  [${t.status}] ${t.name}${t.description ? ': ' + t.description : ''}\n` })
        })

      } else if (entityType === 'business_unit') {
        const [{ data: bu }, { data: phases }] = await Promise.all([
          supabase.from('business_units').select('*, consultancies(name, client_company, industry)').eq('id', entityId).single(),
          supabase.from('phases').select('*, sections(name, status, tasks(name, status))').eq('business_unit_id', entityId).order('phase_order'),
        ])
        context = `Empresa: ${bu?.consultancies?.client_company}\nProyecto: ${bu?.consultancies?.name}\nIndustria: ${bu?.consultancies?.industry ?? ''}\nUnidad de Negocio: ${bu?.name}\n${bu?.description ? 'Descripción: ' + bu.description : ''}\n\n`
        phases?.forEach((ph, idx) => {
          context += `\n=== ETAPA ${idx + 1}: ${ph.name} (${ph.status}, ${ph.progress}%) ===\n`
          ph.sections?.forEach(s => {
            context += `  --- ${s.name} (${s.status}) ---\n`
            s.tasks?.forEach(t => { context += `    [${t.status}] ${t.name}\n` })
          })
        })

      } else {
        // consultancy level
        const [{ data: cons }, { data: bus }] = await Promise.all([
          supabase.from('consultancies').select('*').eq('id', consultancyId).single(),
          supabase.from('business_units').select('*, phases(name, status, progress, sections(name, status, tasks(name, status)))').eq('consultancy_id', consultancyId).order('bu_order'),
        ])
        context = `Empresa: ${cons?.client_company}\nProyecto: ${cons?.name}\nIndustria: ${cons?.industry ?? ''}\nDescripción: ${cons?.description ?? ''}\n\n`
        bus?.forEach(bu => {
          context += `\n╔═══════════════════════════════\n║ UNIDAD DE NEGOCIO: ${bu.name}\n╚═══════════════════════════════\n`
          bu.phases?.forEach((ph, idx) => {
            context += `\n  === ETAPA ${idx + 1}: ${ph.name} (${ph.status}, ${ph.progress}%) ===\n`
            ph.sections?.forEach(s => {
              context += `    --- ${s.name} (${s.status}) ---\n`
              s.tasks?.forEach(t => { context += `      [${t.status}] ${t.name}\n` })
            })
          })
        })
      }

      // Call Supabase Edge Function
      const { data, error: fnError } = await supabase.functions.invoke('ai-report', {
        body: { context, reportType, entityType, consultancyId }
      })
      if (fnError) throw fnError

      // Save report
      await supabase.from('ai_reports').insert({
        consultancy_id: consultancyId,
        entity_type: entityType === 'consultancy' ? 'final' : entityType,
        entity_id: entityId ?? null,
        report_type: reportType,
        content: data.report,
        generated_by: profile.id,
      })

      setReport(data.report)
      setShowModal(true)
    } catch (err) {
      setError(err.message ?? 'Error al generar el informe')
      setShowModal(true)
    } finally {
      setLoading(false)
    }
  }

  function downloadPDF() {
    import('jspdf').then(({ jsPDF }) => {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' })
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 20
      const maxW = pageW - margin * 2

      // Header bar
      doc.setFillColor(31, 41, 91) // brand dark blue
      doc.rect(0, 0, pageW, 30, 'F')

      // Company name
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text('NEXT VALLEY CONSULTANTS', margin, 12)

      // Report title
      doc.setFontSize(14)
      doc.text(label, margin, 22)

      // Date
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }), pageW - margin, 22, { align: 'right' })

      // Content
      doc.setTextColor(30, 30, 30)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')

      const lines = doc.splitTextToSize(report || '', maxW)
      let y = 45

      lines.forEach(line => {
        if (y > pageH - 20) {
          doc.addPage()
          y = 20
        }
        doc.text(line, margin, y)
        y += 5
      })

      // Footer on each page
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text(`Página ${i} de ${totalPages} — Next Valley Consultants`, pageW / 2, pageH - 8, { align: 'center' })
      }

      doc.save(`informe-nvc-${Date.now()}.pdf`)
    })
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        <button
          onClick={generateReport}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-700 to-brand-500 text-white rounded-lg text-sm font-medium hover:from-brand-600 hover:to-brand-400 transition-all shadow-sm disabled:opacity-60"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {loading ? 'Generando...' : label}
        </button>
        <button
          onClick={() => { loadPastReports(); setShowPast(true) }}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 justify-center"
        >
          <ChevronDown size={11} /> Informes anteriores
        </button>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title={label} size="xl">
        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
        ) : (
          <div className="space-y-4">
            <div className="prose prose-sm max-w-none bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-[60vh] overflow-y-auto font-mono leading-relaxed">
              {report}
            </div>
            <button onClick={downloadPDF} className="btn-secondary flex items-center gap-2 text-sm">
              <Download size={15} /> Descargar PDF
            </button>
          </div>
        )}
      </Modal>

      <Modal open={showPast} onClose={() => setShowPast(false)} title="Informes generados anteriormente" size="lg">
        {pastReports.length === 0 ? (
          <p className="text-center text-gray-400 py-6">No hay informes generados aún</p>
        ) : (
          <div className="space-y-3">
            {pastReports.map(r => (
              <div key={r.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-gray-500 capitalize">{r.report_type} · {r.entity_type}</span>
                  <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <p className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">{r.content}</p>
                <button onClick={() => { setReport(r.content); setShowPast(false); setShowModal(true) }}
                  className="text-xs text-brand-600 hover:text-brand-700 mt-2">Ver completo →</button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}
